/**
 * Directories API Contracts
 *
 * Directory management endpoints.
 */

import { z } from 'zod'
import { PaginationMetaSchema, PaginationParamsSchema } from '../common'
import { DirectorySchema, DirectoryEntrySchema, DirectoryWithEntriesSchema } from '../../schemas/entities/directory'

// Re-export for convenience
export { DirectorySchema, DirectoryEntrySchema, DirectoryWithEntriesSchema }
export type { Directory, DirectoryEntry, DirectoryWithEntries } from '../../schemas/entities/directory'

/**
 * Directory List Item - minimal fields returned by list endpoint
 * (Blackbaud API only returns id, name, sort - id is numeric from Blackbaud)
 */
export const DirectoryListItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  sort: z.number().optional(),
  // Whether members of this directory appear in the mobile app. Sourced from the
  // z_RefDirectories mirror (defaults to true when no mirror row exists yet).
  showInApp: z.boolean().optional()
})

export type DirectoryListItem = z.infer<typeof DirectoryListItemSchema>

/**
 * GET /v1/directories
 */
export const GetDirectoriesRequestSchema = PaginationParamsSchema.extend({
  schoolId: z.string().optional()
})

export type GetDirectoriesRequest = z.infer<typeof GetDirectoriesRequestSchema>

export const GetDirectoriesResponseSchema = z.object({
  data: z.array(DirectoryListItemSchema),
  meta: PaginationMetaSchema.optional(),
  message: z.string()
})

export type GetDirectoriesResponse = z.infer<typeof GetDirectoriesResponseSchema>

/**
 * GET /v1/directories/:directoryId
 */
export const GetDirectoryRequestSchema = z.object({
  directoryId: z.string(),
  search: z.string().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
})

export type GetDirectoryRequest = z.infer<typeof GetDirectoryRequestSchema>

export const GetDirectoryResponseSchema = z.object({
  data: DirectoryWithEntriesSchema,
  meta: PaginationMetaSchema.optional(),
  message: z.string()
})

export type GetDirectoryResponse = z.infer<typeof GetDirectoryResponseSchema>

/**
 * PATCH /v1/schools/:schoolId/directories/:directoryId
 */
export const UpdateDirectoryRequestSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  isEnabled: z.boolean().optional(),
  isSearchable: z.boolean().optional(),
  visibleFields: z.array(z.string()).optional()
})

export type UpdateDirectoryRequest = z.infer<typeof UpdateDirectoryRequestSchema>

export const UpdateDirectoryResponseSchema = z.object({
  data: DirectorySchema,
  message: z.string()
})

export type UpdateDirectoryResponse = z.infer<typeof UpdateDirectoryResponseSchema>
